import {
  IncidentOwnershipFilter,
  IncidentUrgencyFilter,
  IncidentMetadataFilter,
} from 'hooks/useMapSearch/useMapSearch.types';
import { differenceInMinutes } from 'date-fns';
import { GeoEventEvacZoneStatus } from 'shared/types';
import { IncidentFiltersState } from 'state/useIncidentFiltersState';
import { WildfireListItem } from 'features/wildfires/wildfire.types';
import { FloodingListItem } from 'features/flooding/flooding.types';
import { NotificationType } from 'features/notifications/notifications.types';

/** Time threshold in hours for considering notes as "recent" */
const RECENT_NOTES_THRESHOLD_HOURS = 24;

/** Containment threshold (percent) above which incidents can be hidden */
export const CONTAINMENT_HIDE_THRESHOLD = 50;

type FilterContext = {
  activeEvacZones: GeoEventEvacZoneStatus[];
};

/**
 * Check if incident matches ownership filter
 */
const matchesOwnershipFilter = (
  incident: WildfireListItem | FloodingListItem,
  filter: IncidentOwnershipFilter,
): boolean => {
  switch (filter) {
    case 'reporter_managed':
      return 'isReporterExternal' in incident
        ? incident.reporterManaged && !incident.isReporterExternal
        : incident.reporterManaged;
    case 'bot_managed':
      return !incident.reporterManaged && incident.externalSource !== null;
    case 'external_reporter':
      return (
        'isReporterExternal' in incident &&
        incident.reporterManaged &&
        incident.isReporterExternal === true
      );
    default:
      return false;
  }
};

/**
 * Check if incident has active evacuations (custom or structured)
 */
const hasActiveEvacuations = (
  incident: WildfireListItem | FloodingListItem,
  context: FilterContext,
): boolean => {
  const { data } = incident;
  const hasCustomEvacs =
    data.hasCustomEvacuationOrders ||
    data.hasCustomEvacuationWarnings ||
    data.hasCustomEvacuationAdvisories ||
    data.hasCustomEvacuationShelterInPlace;

  const hasStructuredEvacs = context.activeEvacZones.some(
    (zone) => zone.geoEventId === incident.id,
  );

  return hasCustomEvacs || hasStructuredEvacs;
};

/**
 * Check if incident matches urgency filter
 */
const matchesUrgencyFilter = (
  incident: WildfireListItem | FloodingListItem,
  filter: IncidentUrgencyFilter,
): boolean => {
  return 'urgency' in incident.data && incident.data.urgency === filter;
};

/**
 * Check if incident has associated shelters (locations)
 */
const hasShelters = (
  incident: WildfireListItem | FloodingListItem,
): boolean => {
  return incident.childGeoEvents.some(
    (child) => child.geoEventType === 'location' && child.isActive,
  );
};

/**
 * Check if incident has recent notes
 */
// @todo remove when removing 'internal_tab_on_incidents' release flag
const hasRecentNotes = (
  incident: WildfireListItem | FloodingListItem,
): boolean => {
  const { reporterOnlyNotesDateModified } = incident.data;
  if (!reporterOnlyNotesDateModified) {
    return false;
  }

  const minutesDiff = differenceInMinutes(
    new Date(),
    new Date(reporterOnlyNotesDateModified),
  );

  return minutesDiff < RECENT_NOTES_THRESHOLD_HOURS * 60;
};

const hasActiveNotes = (
  incident: WildfireListItem | FloodingListItem,
): boolean => incident.hasReporterNotes === true;

/**
 * Check if incident is a prescribed burn (Rx). Non-wildfire incidents
 * (e.g. flooding) have no `isPrescribed` field and are treated as not prescribed.
 */
const isPrescribed = (
  incident: WildfireListItem | FloodingListItem,
): boolean => {
  return 'isPrescribed' in incident.data && incident.data.isPrescribed;
};

/**
 * Check if incident is highly contained (containment above the threshold of 50%).
 * Incidents with no containment value, or non-wildfire incidents (no
 * `containment` field), are treated as not highly contained.
 */
const isHighlyContained = (
  incident: WildfireListItem | FloodingListItem,
): boolean => {
  if (!('containment' in incident.data)) {
    return false;
  }
  const { containment } = incident.data;
  return containment != null && containment > CONTAINMENT_HIDE_THRESHOLD;
};

/**
 * Check if incident is active (not deactivated).
 */
const isActiveIncident = (
  incident: WildfireListItem | FloodingListItem,
): boolean => {
  return incident.isActive;
};

/**
 * Check if incident is not silent (i.e. it notifies). Silent incidents are
 * published without a push notification.
 */
const isSilent = (incident: WildfireListItem | FloodingListItem): boolean => {
  return incident.notificationType === NotificationType.Silent;
};

/**
 * Check if incident matches metadata filter
 */
const matchesMetadataFilter = (
  incident: WildfireListItem | FloodingListItem,
  filter: IncidentMetadataFilter,
  context: FilterContext,
): boolean => {
  switch (filter) {
    case 'active_evacuations':
      return hasActiveEvacuations(incident, context);
    case 'has_shelters':
      return hasShelters(incident);
    // @todo remove when removing 'internal_tab_on_incidents' release flag
    case 'has_recent_notes':
      return hasRecentNotes(incident);
    case 'has_active_notes':
      return hasActiveNotes(incident);
    case 'hide_prescribed_burns':
      return !isPrescribed(incident);
    case 'hide_contained_over_50':
      return !isHighlyContained(incident);
    case 'hide_inactive':
      return isActiveIncident(incident);
    case 'hide_silent':
      return !isSilent(incident);
    default:
      return false;
  }
};

/**
 * Check if incident matches all active filters (AND logic)
 */
export const matchesFilters = (
  incident: WildfireListItem | FloodingListItem,
  filters: IncidentFiltersState,
  context: FilterContext,
): boolean => {
  // If no filters selected, incident matches
  if (
    !filters.ownership &&
    filters.urgency.length === 0 &&
    filters.metadata.length === 0
  ) {
    return true;
  }

  // Ownership filter: incident must match the single ownership filter
  const matchesOwnership =
    !filters.ownership || matchesOwnershipFilter(incident, filters.ownership);

  // Urgency filters: incident must match at least one (OR within urgency)
  const matchesUrgency =
    filters.urgency.length === 0 ||
    filters.urgency.some((filter) => matchesUrgencyFilter(incident, filter));

  // Metadata filters: incident must match all (AND within metadata)
  const matchesMetadata =
    filters.metadata.length === 0 ||
    filters.metadata.every((filter) =>
      matchesMetadataFilter(incident, filter, context),
    );

  // Combined: AND between ownership, urgency, and metadata
  return matchesOwnership && matchesUrgency && matchesMetadata;
};
